// Every user-facing line of the free-tier paywall lives here so the wording
// stays lifetime-shaped ("3 free graphs", never "per month", never "delete to
// make room") wherever a refusal surfaces.

const PAYWALL_CODES = [
    'QUOTA_GRAPHS',
    'QUOTA_CHATS',
    'QUOTA_CHAT_MESSAGES',
    'QUOTA_GRAPH_LOCKED',
    'QUOTA_FILE_SIZE',
    'QUOTA_FILE_COUNT',
    'FEATURE_PAID_ONLY',
]

const TRIGGER_BY_CODE = {
    QUOTA_GRAPHS: 'graphs',
    QUOTA_CHATS: 'ai',
    QUOTA_CHAT_MESSAGES: 'messages',
    QUOTA_GRAPH_LOCKED: 'locked',
    QUOTA_FILE_SIZE: 'upload',
    QUOTA_FILE_COUNT: 'upload',
    FEATURE_PAID_ONLY: 'feature',
    MENU: 'menu',
    CHANGE_PLAN: 'change',
}

export function isPaywallCode(code) {
    return PAYWALL_CODES.includes(code)
}

export function triggerFor(code) {
    return TRIGGER_BY_CODE[code] || 'menu'
}

function plural(count, noun) {
    return `${count} ${noun}${count === 1 ? '' : 's'}`
}

export function reasonLineFor(reason) {
    if (!reason) return 'Get unlimited graphs and AI'
    const limit = typeof reason.limit === 'number' ? reason.limit : null
    switch (reason.code) {
        case 'QUOTA_GRAPHS':
            return `You've created your ${limit ??
                3} free graphs. Upgrade for unlimited graphs.`
        case 'QUOTA_CHATS':
            return `You've used your ${limit ??
                3} free AI chats. Upgrade for unlimited chats.`
        case 'QUOTA_CHAT_MESSAGES':
            return `This chat has reached the free limit of ${limit ??
                10} messages. Upgrade to keep going.`
        case 'QUOTA_GRAPH_LOCKED':
            return typeof reason.total === 'number'
                ? `This graph is locked on the free plan. Upgrade to unlock all ${plural(
                      reason.total,
                      'graph'
                  )}.`
                : 'This graph is locked on the free plan. Upgrade to unlock all your graphs.'
        case 'QUOTA_FILE_SIZE':
            return 'Files this size need a paid plan. Upgrade to import larger files.'
        case 'QUOTA_FILE_COUNT':
            return 'Importing several files at once needs a paid plan. Upgrade to import more.'
        case 'CHANGE_PLAN':
            return 'Change your plan'
        case 'FEATURE_PAID_ONLY':
            return reason.feature === 'unsavedPrivateGraphs'
                ? 'Private, unsaved analyses are available on paid plans. Upgrade to analyse without saving.'
                : 'This feature is available on paid plans. Upgrade to use it.'
        default:
            return 'Get unlimited graphs and AI'
    }
}

// Second line of the refusal dialog: what a paid plan actually buys, phrased
// for the limit that was hit. The dialog links to /upgrade for the rest.
export function upgradeHintFor(reason) {
    switch (reason && reason.code) {
        case 'QUOTA_GRAPHS':
        case 'QUOTA_GRAPH_LOCKED':
            return 'Paid plans include unlimited graphs, unlimited AI chats, larger imports, and more API / MCP access tokens.'
        case 'QUOTA_CHATS':
        case 'QUOTA_CHAT_MESSAGES':
            return 'Paid plans include unlimited AI chats with no per-chat message cap.'
        case 'QUOTA_FILE_SIZE':
        case 'QUOTA_FILE_COUNT':
            return 'Paid plans raise the file size limit and let you import several files at once.'
        case 'CHANGE_PLAN':
            return 'Switch plan at any time — you only pay the difference for the rest of the period.'
        default:
            return 'Paid plans include more API / MCP access tokens, unlimited graphs, unlimited AI chats, and larger imports.'
    }
}

// The AI panel's usage line / blocked block.
export function chatsLeftLine(remaining) {
    return remaining === 1 ? '1 free chat left' : `${remaining} free chats left`
}

export const LAST_FREE_GRAPH_HINT =
    'That was your last free graph — upgrade for unlimited'

export const UNSAVED_GRAPH_LOCK_HINT =
    'Private, unsaved analyses are available on paid plans.'
